/**
 * Installer Lanaya sur l'écran d'accueil : une icône à côté de WhatsApp et TikTok, sans passer par un
 * magasin d'applications ni télécharger 100 Mo. Android (Chrome) propose l'installation par un
 * événement que l'on garde pour le bon moment ; iPhone (Safari) passe par « Partager », que l'on explique.
 */
import { useSyncExternalStore } from 'react';

interface InstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

let deferred: InstallPromptEvent | null = null;
let installed = false;
const listeners = new Set<() => void>();
let version = 0;

function emit() {
  version++;
  for (const fn of listeners) fn();
}

function standalone(): boolean {
  try {
    return window.matchMedia('(display-mode: standalone)').matches || (navigator as Navigator & { standalone?: boolean }).standalone === true;
  } catch {
    return false;
  }
}

/** À appeler tôt (au démarrage) : l'événement d'installation arrive avant le premier écran. */
export function listenForInstall(): void {
  if (typeof window === 'undefined') return;
  installed = standalone();
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    deferred = e as InstallPromptEvent;
    emit();
  });
  window.addEventListener('appinstalled', () => {
    installed = true;
    deferred = null;
    emit();
  });
}

function isIos(): boolean {
  try {
    return /iphone|ipad|ipod/i.test(navigator.userAgent) && !/crios|fxios/i.test(navigator.userAgent);
  } catch {
    return false;
  }
}

export interface InstallState {
  /** Android : le navigateur propose l'installation. */
  canPrompt: boolean;
  /** iPhone (Safari) non installé : expliquer « Partager → Sur l'écran d'accueil ». */
  iosHint: boolean;
  installed: boolean;
  install: () => Promise<boolean>;
}

async function install(): Promise<boolean> {
  const e = deferred;
  if (!e) return false;
  deferred = null;
  emit();
  try {
    await e.prompt();
    const choice = await e.userChoice;
    return choice.outcome === 'accepted';
  } catch {
    return false;
  }
}

export function useInstall(): InstallState {
  useSyncExternalStore(
    (fn) => {
      listeners.add(fn);
      return () => listeners.delete(fn);
    },
    () => version,
    () => version,
  );
  const framed = typeof window !== 'undefined' && window.self !== window.top;
  return {
    canPrompt: Boolean(deferred) && !installed,
    iosHint: !installed && !framed && isIos() && !standalone(),
    installed,
    install,
  };
}
